import { useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { ArrowRight } from "lucide-react"
import { Header } from "./Header"
import { HeroSection } from "./HeroSection"
import { SessionsSection } from "./SessionsSection"
import { SessionPage } from "./SessionPage"
import { Card, CardContent } from "./Card"
import { Footer } from "./Footer"
import { EnquiryModal } from "./EnquiryModal"
import { VizagAgendaModal } from "./VizagAgendaModal"
import { editions, editionRoute } from "../data/events"
import { vizag2026, vizagPresentations } from "../data/vizag2026"

const event = {
  ...vizag2026,
  presentationCount: vizagPresentations.length,
  guestCount: vizagPresentations.filter((session) => session.speakerType === "Guest Speaker").length,
  regularCount: vizagPresentations.filter((session) => session.speakerType !== "Guest Speaker").length,
}

export function VizagEditionPage({ collection = false }) {
  const [showEnquiry, setShowEnquiry] = useState(false)
  const [showAgenda, setShowAgenda] = useState(false)
  const agendaTriggerRef = useRef(null)
  const [showAllPresentations, setShowAllPresentations] = useState(false)
  const { sessionId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const session = vizagPresentations.find((item) => item.id === sessionId)
  const preview = !collection && !sessionId
  const previewSessions = vizagPresentations.slice(0, 4)
  const showPreview = preview && !showAllPresentations && location.hash !== "#sessions"

  const handleAgendaOpen = (event) => {
    agendaTriggerRef.current = event?.currentTarget || null
    setShowAgenda(true)
  }
  const handleAgendaClose = () => {
    setShowAgenda(false)
    if (location.hash === "#agenda") navigate(`${location.pathname}${location.search}`, { replace: true })
  }

  useEffect(() => {
    if (location.hash === "#agenda") setShowAgenda(true)
  }, [location.hash])

  const handlePresentationsClick = () => {
    if (preview) {
      setShowAllPresentations(true)
      document.getElementById("sessions")?.scrollIntoView({ behavior: "smooth" })
    } else {
      navigate(`${event.route}#sessions`)
    }
  }

  useEffect(() => {
    if (showAllPresentations) document.getElementById("presentations-heading")?.focus({ preventScroll: true })
  }, [showAllPresentations])

  useEffect(() => {
    document.title = `${session ? `${session.title} | ` : ""}${event.shortName} — ${event.date}`
    if (location.hash === "#agenda") return
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView()
    else window.scrollTo(0, 0)
  }, [location.pathname, location.hash, session])

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      <Header onBackToHome={() => navigate("/")} currentEdition={event.id} setCurrentEdition={(id) => navigate(editionRoute(id))} onContactClick={() => setShowEnquiry(true)} onPresentationsClick={handlePresentationsClick} onAgendaClick={handleAgendaOpen} />
      <main>
        {sessionId ? session ? (
          <SessionPage session={session} event={event} />
        ) : (
          <div className="mx-auto max-w-4xl px-4 py-20"><h1 className="mb-5 text-3xl font-bold text-green-800">Presentation not found</h1><Link to={event.presentationsRoute} className="text-green-700 underline">Back to CTS 2026 Vizag presentations</Link></div>
        ) : (
          <>
            {preview ? <HeroSection currentEdition={event.id} onPresentationsClick={handlePresentationsClick} onAgendaClick={handleAgendaOpen} /> : (
              <div className="mx-auto max-w-7xl px-4 pt-10 sm:px-6"><Link to={event.route} className="font-medium text-green-700 hover:underline">← Back to CTS 2026 Vizag</Link><h1 className="mt-6 text-3xl font-bold text-green-800">{event.name} — {event.edition}</h1><p className="mt-3 text-green-700">{event.date} | <a href={event.venueUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-green-900">{event.venue}</a></p></div>
            )}
            <SessionsSection sessions={showPreview ? previewSessions : vizagPresentations} event={event} preview={showPreview} onShowAll={() => setShowAllPresentations(true)} />
            {preview && (
              <section className="bg-green-50 py-14" aria-labelledby="event-details-heading">
                <div className="mx-auto max-w-7xl px-4 sm:px-6"><h2 id="event-details-heading" className="mb-8 text-3xl font-bold text-green-800">Event Details</h2><dl className="grid gap-6 text-green-800 sm:grid-cols-3"><div><dt className="text-sm text-green-700">Date</dt><dd className="mt-2 font-semibold"><time dateTime={event.isoDate}>{event.date}</time></dd></div><div><dt className="text-sm text-green-700">Venue</dt><dd className="mt-2 font-semibold"><a href={event.venueUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-green-900">{event.venue}</a><span className="mt-1 block text-sm font-normal">{event.location}</span></dd></div><div><dt className="text-sm text-green-700">Organizer</dt><dd className="mt-2 font-semibold">{event.organizer}</dd></div></dl></div>
              </section>
            )}
          </>
        )}
        <section id="previous-editions" className="scroll-mt-24 py-16" aria-labelledby="previous-editions-heading">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="previous-editions-heading" className="mb-3 text-3xl font-bold text-green-800">Explore Previous Editions</h2>
            <p className="mb-8 text-green-700">Revisit the presentations and recordings from past summits.</p>
            <div className="grid gap-6 md:grid-cols-3">
              {editions.filter((edition) => edition.id !== event.id).map((edition) => (
                <Card key={edition.id} className="h-full border-green-100"><CardContent className="p-6"><h3 className="mb-3 text-xl font-semibold text-green-800"><Link to={edition.route} className="hover:underline">{edition.name}</Link></h3><p className="text-sm text-green-700">{edition.date} · {edition.location}</p><Link to={edition.route} className="mt-5 inline-flex items-center gap-2 font-medium text-green-700">Explore Edition <ArrowRight className="h-4 w-4" /></Link></CardContent></Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      <EnquiryModal open={showEnquiry} onClose={() => setShowEnquiry(false)} />
      <VizagAgendaModal open={showAgenda} onClose={handleAgendaClose} returnFocusRef={agendaTriggerRef} />
      <Footer currentEdition={event.id} />
    </div>
  )
}
